import {
  EditSVG,
  ExcelIconSVG,
  InputSearchIconSVG,
  PDFIconSVG,
  ProjectDeleteSVG,
} from "@/assets/svg";
import DropdownButton from "@/components/DropdownButton";
import { FilterSelectWithRadio } from "@/components/form";
import DateRangePicker from "@/components/form/DateRangePicker";
import AppLayout from "@/components/layout/AppLayout";
import Modal from "@/components/Modal";
import EditTimeEntryForm from "./components/EditTimeEntryForm";
import NoDataContainer from "@/components/NoDataContainer";
import Tooltip from "@/components/Tooltip";
import Tabs from "@/components/Tabs";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { selectUser, selectUserRole } from "@/store/slices/authSlice";
import {
  deleteTimeEntry,
  fetchTimeEntries,
  getTimeEntryDetail,
  resetFilters,
  selectBillableFilter,
  selectDateRangeFilter,
  selectNoTimeEntries,
  selectProjectFilter,
  selectSearchTerm,
  selectTimeEntries,
  selectTimeEntriesLoading,
  selectTimeEntriesPagination,
  selectTotalDuration,
  selectUserFilter,
  setBillableFilter,
  setDateRangeFilter,
  setProjectFilter,
  setSearchTerm,
  setUserFilter,
  selectProjectType,
  setProjectType,
  selectProjectTypeFilter,
  setProjectTypeFilter,
  TimeEntryDisplay,
  updateTimeEntry,
} from "@/store/slices/timeEntriesSlice";
import {
  fetchProjectOptions,
  selectProjectOptions,
} from "@/store/slices/timeEntrySlice";
import { fetchUserOptions, selectUserOptions } from "@/store/slices/userSlice";
import { selectSelectedOrganization } from "@/store/slices/clientOrganizationSlice";
import { ROLES } from "@/utils/constants";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router";
import { errorToast, successToast } from "@/utils/toast";
import { downloadTimeEntryPDFService } from "@/service/time-entry.service";
import { TimeEntryQuery } from "@/types/time-entry.model";
import moment from "moment";

// Define column meta type for TypeScript
type ColumnMeta = {
  isSticky?: boolean;
  width?: string;
  maxWidth?: string;
};

// Billable badge component
const BillableBadge: React.FC<{ billable: boolean }> = ({ billable }) => {
  return (
    <span
      className={`px-2 py-1 rounded-sm text-xs font-medium ${billable ? "bg-[#DDDEFF] text-[#73B5C5]" : "bg-[#E0E0E0] text-[#616161]"
        }`}
    >
      {billable ? "Billable to HLJ" : "Non-billable to HLJ"}
    </span>
  );
};

// Time entries table component using TanStack React Table
const TimeEntriesTable: React.FC<{
  data: TimeEntryDisplay[];
  onEdit: (entry: TimeEntryDisplay) => void;
  onDelete: (entry: TimeEntryDisplay) => void;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
  onPageChange: (page: number) => void;
}> = ({ data, onEdit, onDelete, pagination, onPageChange }) => {
  const columnHelper = createColumnHelper<TimeEntryDisplay>();

  const userRole = useAppSelector(selectUserRole);
  const isClient = userRole === ROLES.CLIENT;

  const columns = React.useMemo(() => {
    const baseColumns = [
      columnHelper.accessor("user_name", {
        header: "Employee/Contractor name",
        cell: (info) => (
          <div
            className="text-wrap"
            title={info.getValue()}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {info.getValue()}
          </div>
        ),
        meta: { width: "232px", maxWidth: "232px" } as ColumnMeta,
      }),
      columnHelper.accessor("clock_in_date", {
        header: "Clock in date",
        cell: (info) =>
          moment(info.getValue(), "DD/MM/YYYY").format("MM/DD/YYYY"),
        meta: { width: "120px" } as ColumnMeta,
      }),
      // columnHelper.accessor("clock_in_time", {
      //   header: "Clock in time",
      //   cell: (info) => info.getValue(),
      //   meta: { width: "120px" } as ColumnMeta,
      // }),
      // columnHelper.accessor("clock_out_date", {
      //   header: "Clock out date",
      //   cell: (info) =>
      //     moment(info.getValue(), "DD/MM/YYYY").format("MM/DD/YYYY"),
      //   meta: { width: "130px" } as ColumnMeta,
      // }),
      // columnHelper.accessor("clock_out_time", {
      //   header: "Clock out time",
      //   cell: (info) => info.getValue(),
      //   meta: { width: "130px" } as ColumnMeta,
      // }),
      columnHelper.accessor("duration", {
        header: "Duration",
        cell: (info) => info.getValue(),
        meta: { width: "90px" } as ColumnMeta,
      }),
      // columnHelper.accessor("type", {
      //   header: "Type",
      //   cell: (info) => info.getValue(), //<TypeBadge type={info.getValue() as string} />,
      //   meta: { width: "126px" } as ColumnMeta,
      // }),
      columnHelper.accessor("client_name", {
        header: "Client Name",
        cell: (info) => (
          <div
            className="text-wrap"
            title={info.getValue()}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {info.getValue()}
          </div>
        ),
        meta: { width: "180px", maxWidth: "180px" } as ColumnMeta,
      }),
      columnHelper.accessor("project_name", {
        header: "Project Name",
        cell: (info) => (
          <div
            className="text-wrap"
            title={info.getValue()}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {info.getValue()}
          </div>
        ),
        meta: { width: "180px", maxWidth: "180px" } as ColumnMeta,
      }),
      columnHelper.accessor("service_item", {
        header: "Service",
        cell: (info) => (
          <div
            className="text-wrap"
            title={info.getValue()}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {info.getValue()}
          </div>
        ),
        meta: { width: "180px", maxWidth: "180px" } as ColumnMeta,
      }),
      columnHelper.accessor("billable", {
        header: "Billable / non-billable",
        cell: (info) => <BillableBadge billable={info.getValue() as boolean} />,
        meta: { width: "175px", maxWidth: "175px" } as ColumnMeta,
      }),
      columnHelper.accessor("notes", {
        header: "Notes",
        cell: (info) => (
          <div
            className="text-wrap"
            title={info.getValue()}
            style={{
              wordBreak: "break-word",
              // overflow: "hidden",
              // textOverflow: "ellipsis",
              // whiteSpace: "nowrap",
            }}
          >
            {info.getValue()}
          </div>
        ),
        meta: { width: "260px", maxWidth: "260px" } as ColumnMeta,
      }),
    ];

    // Only add the action column for non-client users
    if (!isClient) {
      baseColumns.push(
        // @ts-ignore
        columnHelper.display({
          id: "actions",
          header: "Action",
          cell: (info) => (
            <div className="flex bg-white items-center space-x-2.5">
              <Tooltip content="Edit">
                <button
                  className="cursor-pointer"
                  onClick={() => onEdit(info.row.original)}
                >
                  <EditSVG />
                </button>
              </Tooltip>
              <Tooltip content="Delete">
                <button
                  className="cursor-pointer"
                  onClick={() => onDelete(info.row.original)}
                >
                  <ProjectDeleteSVG />
                </button>
              </Tooltip>
            </div>
          ),
          meta: { width: "120px", isSticky: true } as ColumnMeta,
        })
      );
    }

    return baseColumns;
  }, [columnHelper, onEdit, onDelete, isClient]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto relative py-4">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-[#F4F7F9] text-left">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isSticky = (header.column.columnDef.meta as any)
                    ?.isSticky;
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      className={`p-3 text-sm font-[600] text-[#1E1E1E] tracking-wider ${isSticky
                        ? "px-4 sticky right-0 bg-[#F4F7F9] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
                        : ""
                        }`}
                      style={{
                        width: (header.column.columnDef.meta as ColumnMeta)
                          ?.width,
                        minWidth: (header.column.columnDef.meta as ColumnMeta)
                          ?.width,
                        maxWidth: (header.column.columnDef.meta as ColumnMeta)
                          ?.maxWidth,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 border-l-1 border-r-1 border-b-1 border-gray-200">
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  No time entries found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-[#F9FEFF]">
                  {row.getVisibleCells().map((cell) => {
                    const isSticky = (cell.column.columnDef.meta as any)
                      ?.isSticky;
                    return (
                      <td
                        key={cell.id}
                        className={`text-sm whitespace-nowrap text-[#1E1E1E] font-[500] ${isSticky
                          ? "sticky bg-white right-0  my-0.5"
                          : "px-3 py-4"
                          }`}
                        style={{
                          width: (cell.column.columnDef.meta as ColumnMeta)
                            ?.width,
                          minWidth: (cell.column.columnDef.meta as ColumnMeta)
                            ?.width,
                          maxWidth: (cell.column.columnDef.meta as ColumnMeta)
                            ?.maxWidth,
                        }}
                      >
                        {isSticky ? (
                          <div className="p-3 bg-white">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </div>
                        ) : (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-4 text-sm text-[#1E1E1E] font-[600] flex justify-between items-center">
        <div>
          Showing{" "}
          {pagination.currentPage === 1
            ? 1
            : (pagination.currentPage - 1) * pagination.pageSize + 1}{" "}
          to{" "}
          {Math.min(
            pagination.currentPage * pagination.pageSize,
            pagination.totalItems
          )}{" "}
          of {pagination.totalItems} results
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              if (pagination.currentPage > 1) {
                onPageChange(pagination.currentPage - 1);
              }
            }}
            disabled={pagination.currentPage <= 1}
            className="px-3 py-1 w-[78px] h-[34px] text-sm text-[#1E1E1E] bg-[#F5F5F5] rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            Previous
          </button>
          <button
            onClick={() => {
              if (pagination.currentPage < pagination.totalPages) {
                onPageChange(pagination.currentPage + 1);
              }
            }}
            disabled={pagination.currentPage >= pagination.totalPages}
            className="px-3 py-1 w-[78px] h-[34px] text-sm text-white bg-[#73B5C5] rounded-md hover:bg-[#5da7b8] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

const AllTimeEntries: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntryDisplay | null>(
    null
  );
  const [entryToDelete, setEntryToDelete] = useState<TimeEntryDisplay | null>(
    null
  );
  const [modalMode, setModalMode] = useState<"edit" | "delete">("edit");

  const userOptions = useAppSelector(selectUserOptions);
  const projectOptions = useAppSelector(selectProjectOptions);

  // Get time entries data from Redux store
  const timeEntries = useAppSelector(selectTimeEntries);
  const searchTerm = useAppSelector(selectSearchTerm);

  const projectFilter = useAppSelector(selectProjectFilter);
  const userFilter = useAppSelector(selectUserFilter);
  const billableFilter = useAppSelector(selectBillableFilter);
  const dateRangeFilter = useAppSelector(selectDateRangeFilter);
  const projectType = useAppSelector(selectProjectType);
  const projectTypeFilter = useAppSelector(selectProjectTypeFilter);

  const pagination = useAppSelector(selectTimeEntriesPagination);
  const noEntries = useAppSelector(selectNoTimeEntries);
  const isLoading = useAppSelector(selectTimeEntriesLoading);
  const totalDuration = useAppSelector(selectTotalDuration);
  const userRole = useAppSelector(selectUserRole);
  const user = useAppSelector(selectUser);

  const isAdmin = userRole === ROLES.ADMIN;
  const isClient = userRole === ROLES.CLIENT;
  const selectedOrganization = useAppSelector(selectSelectedOrganization);

  const [currentProjectFilter, setCurrentProjectFilter] =
    useState("All projects");
  const [currentUserFilter, setCurrentUserFilter] = useState("All users");
  const [currentBillableFilter, setCurrentBillableFilter] =
    useState("All entries");

  // Tabs are independent from the billable dropdown filter

  // Local state to store date range before dispatching to Redux
  const [tempDateRange, setTempDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({ startDate: "", endDate: "" });

  // Update filter state and trigger API call when filters change
  useEffect(() => {
    // Track if this is a filter change after initial load
    if (initialDataLoaded.current) {
      // Update current filter state for UI
      setCurrentProjectFilter(projectFilter);
      setCurrentUserFilter(userFilter);
      setCurrentBillableFilter(billableFilter);

      // Determine the effective project type to send to API
      const effectiveProjectType = projectType === "" ? projectTypeFilter : projectType;

      dispatch(
        fetchTimeEntries({
          page: 1, // Reset to first page when applying filters
          pageSize: pagination.pageSize,
          search: searchTerm || undefined,
          projectId:
            projectFilter !== "All projects" ? projectFilter : undefined,
          userId: userFilter !== "All users" ? userFilter : undefined,
          isBillable:
            billableFilter === "Billable"
              ? 1
              : billableFilter === "Non-billable"
                ? 0
                : undefined,
          startDate: dateRangeFilter.startDate
            ? moment(dateRangeFilter.startDate).format("DD-MM-YYYY")
            : undefined,
          endDate: dateRangeFilter.endDate
            ? moment(dateRangeFilter.endDate).format("DD-MM-YYYY")
            : undefined,
          projectType: effectiveProjectType || undefined,
        })
      );
    }
  }, [
    projectFilter,
    userFilter,
    billableFilter,
    dateRangeFilter,
    projectTypeFilter,
    projectType,
    dispatch,
    pagination.pageSize,
    // searchTerm,
  ]);

  const handleEditEntry = (entry: TimeEntryDisplay) => {
    setModalMode("edit");

    // Fetch the latest entry details from the API instead of using table data
    dispatch(getTimeEntryDetail(entry.id))
      .unwrap()
      .then((entryData: TimeEntryDisplay) => {
        setEditingEntry(entryData);
        setIsModalOpen(true);
      })
      .catch((error: Error) => {
        console.error("Failed to fetch entry details:", error);
        // Fallback to using the table data if API call fails
        setEditingEntry(entry);
        setIsModalOpen(true);
      });
  };

  const handleDeleteEntry = (entry: TimeEntryDisplay) => {
    setEntryToDelete(entry);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteEntry = () => {
    if (entryToDelete) {
      dispatch(deleteTimeEntry(entryToDelete.id))
        .unwrap()
        .then(() => {
          setIsDeleteModalOpen(false);
          setEntryToDelete(null);
        })
        .catch((error) => {
          console.error("Failed to delete entry:", error);
          // Modal stays open on error
        });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEntry(null);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setEntryToDelete(null);
  };

  // Handle export of time entries as Excel or PDF
  const handleExport = async (type: "csv" | "pdf") => {
    try {
      // Create query params from current filters
      const queryParams: TimeEntryQuery = {
        page: 1,
        pageSize: pagination.pageSize,
        search: searchTerm || undefined,
        projectId: projectFilter !== "All projects" ? projectFilter : undefined,
        userId: userFilter !== "All users" ? userFilter : undefined,
        isBillable:
          billableFilter === "Billable"
            ? 1
            : billableFilter === "Non-billable"
              ? 0
              : undefined,
        startDate: dateRangeFilter.startDate
          ? moment(dateRangeFilter.startDate).format("DD-MM-YYYY")
          : undefined,
        endDate: dateRangeFilter.endDate
          ? moment(dateRangeFilter.endDate).format("DD-MM-YYYY")
          : undefined,
        projectType: projectType,
        // For client users, add orgId from selected organization
        orgId: isClient && selectedOrganization?.client_id ? selectedOrganization.client_id : undefined,
      };

      const response = await downloadTimeEntryPDFService(type, queryParams);

      // Create a blob from the response data
      const blob = new Blob([response.data], {
        type: type === "csv" ? "text/csv" : "application/pdf",
      });

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);

      const formatType = type === "csv" ? "csv" : "pdf";

      // Create a temporary anchor element
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `time-tracker_report.${formatType}`);

      // Append the anchor to the body
      document.body.appendChild(link);

      // Trigger the download
      link.click();

      // Clean up
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      successToast(
        "Export Successful",
        `Time entries exported as ${type.toUpperCase()} successfully`
      );
    } catch (error) {
      console.error(`Error exporting time entries as ${type}:`, error);
    }
  };

  // Use ref to track if initial data has been loaded
  const initialDataLoaded = useRef(false);

  // Create a debounced search function
  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      dispatch(
        fetchTimeEntries({
          page: 1, // Reset to first page when searching
          pageSize: pagination.pageSize,
          search: searchValue || undefined,
          projectId:
            projectFilter !== "All projects" ? projectFilter : undefined,
          userId: userFilter !== "All users" ? userFilter : undefined,
          isBillable:
            billableFilter === "Billable"
              ? 1
              : billableFilter === "Non-billable"
                ? 0
                : undefined,
          startDate: dateRangeFilter.startDate
            ? moment(dateRangeFilter.startDate).format("DD-MM-YYYY")
            : undefined,
          endDate: dateRangeFilter.endDate
            ? moment(dateRangeFilter.endDate).format("DD-MM-YYYY")
            : undefined,
          projectType: projectType === "" ? projectTypeFilter : projectType,
        })
      );
    }, 500),
    [
      dispatch,
      pagination.pageSize,
      projectFilter,
      userFilter,
      billableFilter,
      dateRangeFilter,
      projectTypeFilter,
      projectType,
    ]
  );

  // Handle search term changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    dispatch(setSearchTerm(value));
    debouncedSearch(value);
  };

  // Handle Organization changes (Project Options)
  useEffect(() => {
    if (!isAdmin && selectedOrganization?.client_id) {
      if (isClient) {
        dispatch(
          fetchProjectOptions({
            search: "",
            clientId: selectedOrganization.client_id,
          })
        );
      } else {
        dispatch(
          fetchProjectOptions({
            search: "",
            userId: selectedOrganization.client_id,
          })
        );
      }
    }
  }, [dispatch, isAdmin, isClient, selectedOrganization?.client_id]);

  // Fetch time entries and options on component mount (only once)
  useEffect(() => {
    // Only run this effect on initial mount
    const loadInitialData = async () => {
      // Set flag first to prevent other effects from triggering API calls
      initialDataLoaded.current = true;

      if (isAdmin) {
        // Load options first (these don't depend on filters)
        await Promise.all([
          dispatch(fetchUserOptions({ showAll: false })),
          dispatch(fetchProjectOptions({ search: "" })),
        ]);
      }

      // Check if there's a projectId in the URL query parameters
      const projectIdFromUrl = searchParams.get("projectId");

      if (projectIdFromUrl) {
        // Set the project filter if projectId is provided in URL
        dispatch(setProjectFilter(projectIdFromUrl));
        setCurrentProjectFilter(projectIdFromUrl);

        // Load time entries with the project filter
        dispatch(
          fetchTimeEntries({
            page: 1,
            pageSize: 20,
            projectId: projectIdFromUrl,
            projectType: projectType,
            orgId:
              isClient && selectedOrganization?.client_id
                ? selectedOrganization.client_id
                : undefined,
          })
        );
      } else {
        // Load time entries without filters
        dispatch(
          fetchTimeEntries({
            page: 1,
            pageSize: 20,
            projectType: projectType === "" ? projectTypeFilter : projectType,
            orgId:
              isClient && selectedOrganization?.client_id
                ? selectedOrganization.client_id
                : undefined,
          })
        );
      }
    };

    if (!initialDataLoaded.current) {
      loadInitialData();
    }

    // Reset filters when component unmounts
    return () => {
      dispatch(resetFilters());
    };

    // Re-run this effect if the URL search params change
  }, [searchParams, dispatch, selectedOrganization]);

  // Reset all filters and fetch time entries with no filters
  const handleResetFilter = () => {
    // Skip the useEffect trigger by setting initialDataLoaded to false temporarily
    initialDataLoaded.current = false;

    // Reset all filters
    dispatch(resetFilters());

    // Directly fetch time entries with reset filters
    dispatch(
      fetchTimeEntries({
        page: 1,
        pageSize: pagination.pageSize,
        projectType: projectType === "" ? projectTypeFilter : projectType,
      })
    );

    // Re-enable the useEffect trigger
    setTimeout(() => {
      initialDataLoaded.current = true;
    }, 100);
  };

  // Billable filter options
  const billableOptions = [
    { value: "All entries", label: "All entries" },
    { value: "Billable", label: "Billable to HLJ" },
    { value: "Non-billable", label: "Non-billable to HLJ" },
  ];

  return (
    <AppLayout
      title="All time entries"
      description="View all recorded time entries across projects"
      contentClassName={`min-h-[90%] ${pagination.totalPages ? "" : "flex items-center justify-center"
        }`}
      rightActions={
        <>
          <DropdownButton
            label="Export as"
            disabled={pagination.totalItems === 0}
            options={[
              {
                icon: <ExcelIconSVG className="w-5 h-5" />,
                label: "CSV file",
                onClick: () => {
                  handleExport("csv");
                },
              },
              {
                icon: <PDFIconSVG className="w-5 h-5" />,
                label: "PDF file",
                onClick: () => {
                  handleExport("pdf");
                },
              },
            ]}
            buttonClassName="h-[38px]"
          />
        </>
      }
    >
      <div className="p-4">
        <Tabs
          items={[
            { label: "All", value: "" },
            { label: "Billable", value: "billable" },
            {
              label: isClient ? "Fixed Scope" : "Non-billable",
              value: "non-billable",
            },
            { label: "Block of time", value: "block of time" },
          ]}
          value={projectType}
          onChange={(val) => dispatch(setProjectType(val))}
          className="mb-4"
        />
        {noEntries ? (
          <NoDataContainer
            title="No time entries found"
            description="There are no time entries in the system yet."
          />
        ) : (
          <div className="bg-white rounded-lg">
            {/* Filters */}
            <div className="py-4 flex flex-wrap items-center justify-between gap-2">
              <div className="relative flex-grow max-w-[246px]">
                <input
                  type="text"
                  placeholder="Search"
                  className="w-full h-[42px] px-3 pl-9 bg-[#F9FBFF] rounded-[6px] focus:outline-none focus:border-[#73B5C5] placeholder:text-[#B5B7C0]"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                  <InputSearchIconSVG />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="flex flex-wrap gap-3 mr-3">
                  {projectType === "" && (
                    <div className="min-w-[142px]">
                      <FilterSelectWithRadio
                        options={[
                          { value: "", label: "All types" },
                          { value: "billable", label: "Billable" },
                          {
                            value: "non-billable",
                            label: isClient ? "Fixed Scope" : "Non-billable",
                          },
                          { value: "block of time", label: "Block of time" },
                        ]}
                        value={projectTypeFilter}
                        onChange={(value) => dispatch(setProjectTypeFilter(value))}
                        width="w-[180px]"
                      />
                    </div>
                  )}
                  <div className="min-w-[142px]">
                    <FilterSelectWithRadio
                      options={[
                        { value: "All projects", label: "All projects" },
                        ...projectOptions.map((project) => ({
                          value: project.id,
                          label: project.name,
                        })),
                      ]}
                      value={projectFilter}
                      onChange={(value) => dispatch(setProjectFilter(value))}
                      width="w-[220px]"
                    />
                  </div>
                  <div className="min-w-[142px]">
                    <FilterSelectWithRadio
                      options={billableOptions}
                      value={billableFilter}
                      onChange={(value) => dispatch(setBillableFilter(value))}
                    />
                  </div>
                  {isAdmin && (
                    <div className="min-w-[142px]">
                      <FilterSelectWithRadio
                        options={[
                          {
                            value: "All users",
                            label: "All users",
                          },
                          ...userOptions.map((user) => ({
                            value: user.id,
                            label: user.full_name,
                          })),
                        ]}
                        value={userFilter}
                        onChange={(value) => dispatch(setUserFilter(value))}
                        width="w-[220px]"
                      />
                    </div>
                  )}
                  <div className="min-w-[220px] relative">
                    <DateRangePicker
                      startDate={
                        tempDateRange.startDate || dateRangeFilter.startDate
                      }
                      endDate={tempDateRange.endDate || dateRangeFilter.endDate}
                      onChange={(dates) => {
                        // Update local state first
                        setTempDateRange({
                          startDate: dates.startDate,
                          endDate: dates.endDate,
                        });

                        // Only dispatch to Redux (which triggers API call) when both dates are selected
                        if (dates.startDate && dates.endDate) {
                          dispatch(
                            setDateRangeFilter({
                              startDate: dates.startDate,
                              endDate: dates.endDate,
                            })
                          );
                          // Reset temp state after dispatching
                          setTempDateRange({ startDate: "", endDate: "" });
                        }
                      }}
                    />
                  </div>
                </div>

                <button
                  className={`h-[38px] px-4 text-sm text-[#6C6C6C] bg-white rounded-[6px] ${currentProjectFilter !== "All projects" ||
                    currentUserFilter !== "All users" ||
                    currentBillableFilter !== "All entries" ||
                    dateRangeFilter.startDate !== "" ||
                    dateRangeFilter.endDate !== ""
                    ? "hover:bg-gray-50 cursor-pointer"
                    : "opacity-50 cursor-not-allowed"
                    }`}
                  onClick={handleResetFilter}
                  disabled={
                    currentProjectFilter === "All projects" &&
                    currentUserFilter === "All users" &&
                    currentBillableFilter === "All entries" &&
                    dateRangeFilter.startDate === "" &&
                    dateRangeFilter.endDate === ""
                  }
                >
                  Reset filter
                </button>
              </div>
            </div>
            {totalDuration !== "00 hrs" && (
              <div className="px-4 py-3 bg-[#F9FBFF] rounded-md mb-4">
                <p className="text-sm font-semibold text-[#1E1E1E]">
                  Total Duration: <span className="text-[#73B5C5]">{totalDuration}</span>
                </p>
              </div>
            )}
            {/* Table with TanStack React Table */}
            <TimeEntriesTable
              data={timeEntries}
              onEdit={handleEditEntry}
              onDelete={handleDeleteEntry}
              pagination={pagination}
              onPageChange={(page) => {
                dispatch(
                  fetchTimeEntries({
                    page,
                    pageSize: pagination.pageSize,
                    search: searchTerm || undefined,
                    projectId:
                      projectFilter !== "All projects"
                        ? projectFilter
                        : undefined,
                    userId: userFilter !== "All users" ? userFilter : undefined,
                    isBillable:
                      billableFilter === "Billable"
                        ? true
                        : billableFilter === "Non-billable"
                          ? false
                          : undefined,
                    startDate: dateRangeFilter.startDate ? moment(dateRangeFilter.startDate).format("DD-MM-YYYY") : undefined,
                    endDate: dateRangeFilter.startDate ? moment(dateRangeFilter.endDate).format("DD-MM-YYYY") : undefined,
                    projectType: projectType === "" ? projectTypeFilter : projectType,
                  })
                );
              }}
            />
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        title="Delete time entry"
        width="min(95%, 550px)"
      >
        <div className="p-4">
          <p className="text-center font-medium text-[#212121] text-[18px] mb-6">
            Are you sure you want to delete this time entry?
          </p>

          <div className="flex justify-center gap-4">
            <button
              onClick={confirmDeleteEntry}
              className="px-3 py-2 w-[98px] text-[14px] font-medium h-[36px] bg-[#73B5C5] text-white rounded-md hover:bg-[#5da7b8] transition-colors cursor-pointer"
            >
              Yes
            </button>
            <button
              onClick={handleCloseDeleteModal}
              className="px-3 py-2 w-[98px] h-[36px] text-[14px] font-medium bg-[#F5F5F5] text-gray-800 rounded-md hover:bg-gray-200 transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Edit time entry"
        width="min(95%, 868px)"
        contentClassName="p-0"
      >
        {editingEntry && (
          <EditTimeEntryForm
            initialData={editingEntry}
            onSubmit={(value) => {
              const finalPayload = {
                ...value,
                user_id: editingEntry.user_id,
                project_id: editingEntry.project_id,
              };

              if (
                value.clock_in_date &&
                value.clock_in_time &&
                value.clock_out_date &&
                value.clock_out_time
              ) {
                delete finalPayload.duration;
              }


              dispatch(updateTimeEntry(finalPayload as any))
                .unwrap()
                .then(() => {
                  handleCloseModal();
                });
            }}
            onCancel={handleCloseModal}
          />
        )}
      </Modal>
    </AppLayout>
  );
};

export default AllTimeEntries;
